import { type SeatStatus } from '../bus/types';
import { Gender, type SubmitBuyerPassenger } from '@store/form';
import BillHandler from '@itinerary/BillHandler';
import { ReserveType } from '@utils/itinerary';
import { PaxType } from '@utils/types';
import {
    JourneyApiApiRegisterInfoPost,
    ServiceType,
    type TripRegisterInfo,
    type TripReserveInfo,
} from '@clients/MultiRouteApi';
import { type RegisterPassengersParams } from '@store/invoice';

export default class RouteBillHandler extends BillHandler {
    constructor(public reserveInfos: TripReserveInfo[]) {
        super();
    }

    override async registerPaxInfo(params: {
        pax: SubmitBuyerPassenger[];
        email: string;
        mobile: string;
        destinationMobile: string;
        billId: number;
        billCode?: string;
        customData: RegisterPassengersParams['customData'];
    }): Promise<{
        result: boolean;
        departureTime: Date | null;
        data?: any;
    }> {
        const { summary } = params.customData;
        const tripRegisterInfos = this.reserveInfos.reduce((prev, next) => {
            switch (next.serviceType) {
                case ServiceType.NUMBER_0:
                    return [
                        ...prev,
                        {
                            serviceType: next.serviceType,
                            tripId: next.tripId,
                            reserveId: next.reserveId,
                            serviceId: next.serviceId,
                        },
                    ];

                case ServiceType.NUMBER_1:
                    return [
                        ...prev,
                        {
                            serviceType: next.serviceType,
                            tripId: next.tripId,
                            reserveId: next.reserveId,
                            serviceId: next.serviceId,
                            trainCaptchaInput: params.customData.captcha,
                        },
                    ];

                case ServiceType.NUMBER_2:
                    return [
                        ...prev,
                        {
                            serviceType: next.serviceType,
                            tripId: next.tripId,
                            reserveId: next.reserveId,
                            serviceId: next.serviceId,
                            busSeats: summary.map((seat, index) => ({
                                status: seat.status as unknown as SeatStatus,
                                number: seat.numbers?.[next.serviceId || 0],
                                passengerNationalCode:
                                    params.pax[index].nationalCode,
                            })),
                        },
                    ];
                default:
                    return prev;
            }
        }, [] as TripRegisterInfo[]);

        const { data } = await JourneyApiApiRegisterInfoPost({
            registerInfoRequest: {
                billId: params.billId,
                billCode: params?.billCode || '',
                email: params.email,
                phone: params.mobile,
                mobile: params.mobile,
                adultCount: params.pax.filter(
                    ({ type }) => type === PaxType.Adult
                ).length,
                childCount: params.pax.filter(
                    ({ type }) => type === PaxType.Child
                ).length,
                infantCount: params.pax.filter(
                    ({ type }) => type === PaxType.Infant
                ).length,
                tripRegisterInfos,
                passengers: [
                    ...params.pax.map(passenger => ({
                        paxType:
                            passenger.type === PaxType.Adult
                                ? 'ADL'
                                : passenger.type === PaxType.Child
                                  ? 'CHD'
                                  : 'INF',
                        nationality: passenger.nationalityCode,
                        // passportIssuer: passenger.passpo,
                        firstName: passenger.firstName,
                        lastName: passenger.lastName,
                        birthDay: passenger.birthDate,
                        male: passenger.gender === Gender.Male,
                        nationalCode: passenger.nationalCode,
                        passportNumber: passenger.passportNumber,
                        passportExpiration: passenger.passportExpiry,
                        persianFirstName: passenger.persianFirstName,
                        persianLastName: passenger.persianLastName,
                    })),
                ],
                trainCaptchaInput: params.customData.captcha,
            },
        });

        return {
            result: !!data.result,
            departureTime: null,
            data: undefined,
        };
    }

    override async prepayReservation(_billCode: string) {
        await Promise.resolve();
    }

    override get isSingleForRoundTrip(): boolean {
        return true;
    }

    override concreteType? = ReserveType.Route;
}
